const MAX_RECENT_EVENT_IDS = 300;
const MAX_RECENT_MESSAGE_IDS = 500;

const listeners = new Set();
const recentEventIds = new Set();
const recentMessageIds = new Set();

let currentOpenChatPk = null;

function addBounded(setRef, value, maxSize) {
  if (!value) return;
  if (setRef.has(value)) return;
  setRef.add(value);
  if (setRef.size <= maxSize) return;
  const oldest = setRef.values().next().value;
  setRef.delete(oldest);
}

function normalizeText(value) {
  return String(value || "").trim();
}

function buildFallbackEventId(payload) {
  const type = normalizeText(payload?.type);
  const chatPk = normalizeText(payload?.chatPk || payload?.chat_id);
  const messageId = normalizeText(payload?.messageId || payload?.message_id);
  const messageSk = normalizeText(payload?.messageSk || payload?.sk);
  const status = normalizeText(payload?.status || payload?.chatStatus);
  const activityAt = normalizeText(
    payload?.activityAt ||
      payload?.sentAt ||
      payload?.updatedAt ||
      payload?.timestamp ||
      payload?.createdAt
  );
  return [type, chatPk, messageId, messageSk, status, activityAt].join("|");
}

export function extractUserChannelPayload(data) {
  const evt = data?.event;
  return evt?.payload ?? evt?.events?.[0] ?? evt ?? data?.payload ?? data ?? null;
}

export function normalizeCommunicationEvent(rawPayload) {
  if (!rawPayload || typeof rawPayload !== "object") return null;

  const type = normalizeText(rawPayload?.type);
  if (!type) return null;

  const normalized = {
    type,
    eventId: normalizeText(rawPayload?.eventId || rawPayload?.event_id || rawPayload?.id),
    chatPk: normalizeText(rawPayload?.chatPk || rawPayload?.chat_id || rawPayload?.pk),
    messageId: normalizeText(rawPayload?.messageId || rawPayload?.message_id),
    messageSk: normalizeText(rawPayload?.messageSk || rawPayload?.sk),
    messageFrom: normalizeText(
      rawPayload?.messageFrom ||
        rawPayload?.message_from ||
        rawPayload?.senderId ||
        rawPayload?.sender_id
    ),
    status: normalizeText(rawPayload?.status || rawPayload?.chatStatus || rawPayload?.chat_status),
    initiatedBy: normalizeText(
      rawPayload?.initiatedBy ||
        rawPayload?.initiated_by ||
        rawPayload?.requestorId ||
        rawPayload?.requestor_id
    ),
    actedBy: normalizeText(
      rawPayload?.actedBy ||
        rawPayload?.acted_by ||
        rawPayload?.actorId ||
        rawPayload?.actor_id
    ),
    activityAt: normalizeText(
      rawPayload?.activityAt ||
        rawPayload?.sentAt ||
        rawPayload?.updatedAt ||
        rawPayload?.timestamp ||
        rawPayload?.createdAt
    ),
    sentAt: normalizeText(rawPayload?.sentAt || rawPayload?.timestamp),
    preview: normalizeText(
      rawPayload?.preview ||
        rawPayload?.content ||
        rawPayload?.text ||
        rawPayload?.discussionTopic ||
        rawPayload?.discussion_topic
    ),
    raw: rawPayload,
  };

  if (!normalized.eventId) {
    normalized.eventId = buildFallbackEventId(normalized);
  }

  return normalized;
}

export function publishCommunicationEvent(rawPayload) {
  const payload = normalizeCommunicationEvent(rawPayload);
  if (!payload) return null;

  if (payload.eventId && recentEventIds.has(payload.eventId)) {
    return null;
  }
  addBounded(recentEventIds, payload.eventId, MAX_RECENT_EVENT_IDS);

  if (payload.type === "chat_message" && payload.messageId) {
    if (recentMessageIds.has(payload.messageId)) return null;
    addBounded(recentMessageIds, payload.messageId, MAX_RECENT_MESSAGE_IDS);
  }

  listeners.forEach((listener) => {
    try {
      listener(payload);
    } catch (error) {
      console.error("communication realtime listener failed:", error);
    }
  });

  return payload;
}

export function subscribeCommunicationEvents(listener) {
  if (typeof listener !== "function") return () => {};
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function setCurrentOpenChatPk(chatPk) {
  const normalized = normalizeText(chatPk);
  currentOpenChatPk = normalized || null;
}

export function clearCurrentOpenChatPk() {
  currentOpenChatPk = null;
}

export function getCurrentOpenChatPk() {
  return currentOpenChatPk;
}
